/**
 * /free-tools — Free Tools Hub.
 *
 * v34 (Mar 2026): converted from grey-mix to strictly DARK (matches Landing).
 * The page previously used `bg-white` outer with `bg-slate-900` inner cards,
 * producing a washed-out grey hybrid that looked broken next to the Landing.
 *
 * Now: strictly slate-950 dark with emerald accents — same palette as
 * `/` and `/honest-scorecard`.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import {
  ArrowLeft, ArrowRight, Calculator, Sparkles,
  GitBranch, Tag, BarChart3, Receipt, Award, Database,
  TrendingUp,
} from 'lucide-react';

const TOOLS = [
  {
    path: '/bill-score',
    label: 'Bill Score Quiz',
    desc: 'Take a 90-second quiz, get a A–F letter grade on your cloud + AI spend health.',
    icon: Award,
    badge: 'NEW',
    group: 'Assessment',
  },
  {
    path: '/snowflake-health-score',
    label: 'Snowflake Health Score',
    desc: 'Instant readout on warehouse efficiency, idle credits, and query waste.',
    icon: Database,
    group: 'Assessment',
  },
  {
    path: '/free-snowflake-audit',
    label: 'Free Snowflake Audit',
    desc: 'Get a deep audit emailed to you within 24 hours. No signup.',
    icon: Database,
    badge: 'POPULAR',
    group: 'Assessment',
  },
  {
    path: '/ai-profitability',
    label: 'AI Feature Profitability',
    desc: 'Calculate the per-call margin of each AI feature. Spot loss-makers in 2 minutes.',
    icon: Sparkles,
    badge: 'NEW',
    group: 'AI Cost',
  },
  {
    path: '/prompt-cost-predictor',
    label: 'Prompt Cost Predictor',
    desc: 'Paste a prompt → see what it costs across 11 LLMs side-by-side.',
    icon: Sparkles,
    group: 'AI Cost',
  },
  {
    path: '/prompt-playground',
    label: 'Prompt Playground',
    desc: 'Same prompt, every flagship model, one screen. GPT-5.2 · Claude 4.6 · Gemini 3 — see latency, tokens & cost side-by-side.',
    icon: Sparkles,
    badge: 'NEW',
    group: 'AI Cost',
  },
  {
    path: '/fine-tuning-roi',
    label: 'Fine-Tuning ROI Calculator',
    desc: 'Should you fine-tune? Inputs: token volume, model, gain %. Output: payback.',
    icon: Sparkles,
    group: 'AI Cost',
  },
  {
    path: '/decision-simulator',
    label: 'Decision Simulator',
    desc: 'Rank 6 cost-saving scenarios by value × risk. Get the TOP MOVE badge.',
    icon: BarChart3,
    group: 'Decisions',
  },
  {
    path: '/savings-calculator',
    label: 'Savings Calculator',
    desc: 'Type your current spend + size. We project realistic savings ranges.',
    icon: Calculator,
    group: 'Decisions',
  },
  {
    path: '/commitment-coach',
    label: 'Commitment Posture Coach',
    desc: 'Are you over- or under-committed on SP / RI / CUD? Get an action plan.',
    icon: TrendingUp,
    group: 'Decisions',
  },
  {
    path: '/pr-cost-predictor',
    label: 'PR Cost Predictor',
    desc: 'Paste a Terraform plan → see the $ delta before merge. Engineer-friendly.',
    icon: GitBranch,
    badge: 'DEV',
    group: 'Engineering',
  },
  {
    path: '/cost-import',
    label: 'Cost CSV Import',
    desc: 'Upload AWS / Azure / GCP CSV. Get instant breakdowns + waste flags.',
    icon: Receipt,
    group: 'Engineering',
  },
  {
    path: '/tag-drift',
    label: 'Tag Drift Scanner',
    desc: 'Find untagged resources eating into your chargeback accuracy.',
    icon: Tag,
    group: 'Engineering',
  },
  {
    path: '/benchmarks',
    label: 'Industry Benchmarks',
    desc: 'See how your spend compares to peers in your industry + size band.',
    icon: BarChart3,
    group: 'Insights',
  },
  {
    path: '/engineer-savings-wall',
    label: 'Engineer Savings Wall',
    desc: 'Public leaderboard of biggest cost wins by engineers across CARTIE.',
    icon: Award,
    group: 'Insights',
  },
  {
    path: '/badge',
    label: 'README Cost Badge',
    desc: 'Drop a live cloud-cost badge into your GitHub README. SVG, free, public.',
    icon: Award,
    badge: 'OSS',
    group: 'Engineering',
  },
];

const GROUPS = ['Assessment', 'AI Cost', 'Decisions', 'Engineering', 'Insights'];

const BadgeChip = ({ variant }) => {
  const styles = {
    NEW: 'bg-emerald-500 text-slate-950',
    POPULAR: 'bg-amber-500 text-slate-950',
    DEV: 'bg-emerald-500 text-slate-950',
    OSS: 'bg-slate-200 text-slate-950',
  };
  return <span className={`text-[10px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded ${styles[variant] || 'bg-slate-700 text-slate-200'}`}>{variant}</span>;
};

const FreeTools = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="free-tools-page">
      <SEOHead
        title="15 Free Cloud + AI Cost Tools — CARTIEAI"
        description="A growing library of 15 free FinOps tools: Bill Score Quiz, AI Feature P&L Calculator, Prompt Cost Predictor across 11 LLMs, Decision Simulator, Snowflake Audit, Commitment Coach, and more. No signup."
        canonical="https://cartieai.com/free-tools"
        keywords="free finops tools, free cloud cost calculator, free snowflake audit, ai cost calculator, prompt cost calculator"
      />

      {/* Sticky nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-slate-950/80 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-300 hover:text-white" data-testid="free-tools-back">
            <ArrowLeft className="w-4 h-4" /> CARTIEAI
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/pricing" className="text-slate-400 hover:text-white">Pricing</Link>
            <Link to="/login" data-testid="free-tools-cta-signin">
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
                Sign in <ArrowRight className="w-3 h-3 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-14">
        {/* Hero — strictly dark to match Landing */}
        <div className="text-center max-w-3xl mx-auto">
          <Badge className="mb-4 bg-emerald-500/10 text-emerald-300 border-emerald-500/30 px-3 py-1 text-xs font-semibold">
            <Sparkles className="w-3 h-3 mr-1.5 inline" /> Free Tools
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight leading-[1.1]" data-testid="free-tools-h1">
            15 free tools.<br />
            <span className="text-emerald-300 drop-shadow-[0_0_18px_rgba(16,185,129,0.45)]">No signup. No card.</span>
          </h1>
          <p className="text-slate-300 mt-5 text-lg leading-relaxed">
            Every tool answers one narrow FinOps question — quickly, free, forever.<br />
            <span className="text-slate-400 text-sm">The paid product is the running process: continuous monitoring, allocation, chargeback, and auto-fixes. Try the free tools first.</span>
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-7">
            <Link to="/bill-score" data-testid="free-tools-cta-bill-score">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-7 shadow-lg shadow-emerald-500/20">
                Start with the Bill Score Quiz <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10 hover:text-white hover:border-emerald-400 px-7">
                Or jump to Pricing
              </Button>
            </Link>
          </div>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-3 gap-4 mt-14 mb-14 max-w-3xl mx-auto">
          <div className="text-center">
            <div className="text-3xl font-extrabold text-emerald-300 tabular-nums drop-shadow-[0_0_12px_rgba(16,185,129,0.4)]">15</div>
            <div className="text-xs text-slate-400 mt-1">Free tools</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-extrabold text-emerald-300 tabular-nums drop-shadow-[0_0_12px_rgba(16,185,129,0.4)]">0</div>
            <div className="text-xs text-slate-400 mt-1">Signups required</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-extrabold text-emerald-300 tabular-nums drop-shadow-[0_0_12px_rgba(16,185,129,0.4)]">~60s</div>
            <div className="text-xs text-slate-400 mt-1">To first answer</div>
          </div>
        </div>

        {/* Tools by group */}
        <div className="space-y-12">
          {GROUPS.map((g) => {
            const inGroup = TOOLS.filter((t) => t.group === g);
            return (
              <section key={g} data-testid={`free-tools-group-${g.toLowerCase().replace(' ', '-')}`}>
                <div className="flex items-baseline gap-3 mb-4">
                  <h2 className="text-2xl font-bold text-white">{g}</h2>
                  <span className="text-sm text-slate-500">{inGroup.length} tool{inGroup.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {inGroup.map((t) => {
                    const Icon = t.icon;
                    return (
                      <Link key={t.path} to={t.path} className="block group" data-testid={`free-tool-card-${t.path.slice(1)}`}>
                        <Card className="bg-slate-900/60 border-white/10 hover:border-emerald-500/50 hover:-translate-y-0.5 hover:bg-emerald-500/[0.04] transition-all h-full">
                          <CardContent className="p-5">
                            <div className="flex items-start justify-between gap-2 mb-3">
                              <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center">
                                <Icon className="w-5 h-5 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
                              </div>
                              {t.badge && <BadgeChip variant={t.badge} />}
                            </div>
                            <div className="font-bold text-white group-hover:text-emerald-300 transition-colors">{t.label}</div>
                            <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{t.desc}</p>
                            <div className="flex items-center gap-1.5 mt-3 text-xs text-emerald-300 font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                              Open tool <ArrowRight className="w-3 h-3" />
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <Card className="mt-16 bg-slate-900/60 border-emerald-500/30 overflow-hidden relative" data-testid="free-tools-bottom-cta">
          {/* Soft emerald glow */}
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
          <CardContent className="p-8 sm:p-12 text-center relative">
            <Badge className="mb-3 bg-emerald-500/10 text-emerald-300 border-emerald-500/30 px-3 py-1 text-xs font-semibold">Upgrade Path</Badge>
            <h3 className="text-3xl font-extrabold text-white tracking-tight leading-[1.15]">
              Free tools = calculators.<br />
              <span className="text-emerald-300 drop-shadow-[0_0_18px_rgba(16,185,129,0.45)]">CARTIEAI Pro = the running process.</span>
            </h3>
            <p className="text-slate-400 text-base mt-4 max-w-2xl mx-auto leading-relaxed">
              The 15 tools above each answer one question, once. The paid product connects to your cloud + AI accounts and runs continuously — monitoring, allocating, alerting, and auto-fixing 24/7.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-7">
              <Link to="/pricing" data-testid="free-tools-cta-pricing">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-7 shadow-lg shadow-emerald-500/20">
                  Start 7-day free trial <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/vs">
                <Button size="lg" variant="outline" className="border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10 hover:text-white hover:border-emerald-400 px-7">
                  Compare us vs alternatives
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default FreeTools;
